import { useEffect, useRef, useState } from 'react';


function useVersionCheck(interval = 60000) {

        const [version, setVersion] = useState(null);
        const [updateAvailable, setUpdateAvailable] = useState(false);
        const currentVersion = useRef(null);
        const latestVersion = useRef(null);

        useEffect(() => {
                if (process.env.NODE_ENV !== 'production') return;
                if ('serviceWorker' in navigator) {
                        navigator.serviceWorker.getRegistrations().then((regs) => {
                                regs.forEach((reg) => reg.unregister());
                        });
                }

                const fetchVersion = async () => {
                        try {
                                const response = await fetch(`/meta.json?t=${Date.now()}`, { cache: 'no-store' });
                                const data = await response.json();
                                const remoteVersion = data.version;
                                if (!currentVersion.current) {
                                        currentVersion.current = remoteVersion;
                                        latestVersion.current = remoteVersion;
                                        setVersion(remoteVersion);
                                        return;
                                }

                                if (currentVersion.current !== remoteVersion) {
                                        latestVersion.current = remoteVersion;
                                        setUpdateAvailable(true);
                                }

                        } catch (error) {
                                console.error('Version check failed', error);
                        }
                };

                fetchVersion();
                const intervalId = setInterval(fetchVersion, interval);

                return () => clearInterval(intervalId);
        }, [interval]);

        // Force the NEW build to load.
        //
        // window.location.reload(true) did not do this: the `true` (forceReload) flag
        // was Firefox-only, is gone from the spec, and is silently ignored by the
        // Android WebView and WKWebView. So it ran an ordinary reload, which may serve
        // index.html from cache — and a cached index.html still points at the OLD
        // hashed chunks, so the old app loads again. That is why reloading sometimes
        // "worked" but showed the old UI.
        //
        // Order matters: purge the caches first, then navigate with a cache-busting
        // query so index.html itself cannot come from cache. Never awaits forever —
        // a failed cleanup must still reload.
        const reloadPage = async () => {
                const cleanup = (async () => {
                        try {
                                if ('caches' in window) {
                                        const keys = await caches.keys();
                                        await Promise.all(keys.map((key) => caches.delete(key)));
                                }
                        } catch (error) {
                                console.error('Cache purge failed', error);
                        }

                        try {
                                if ('serviceWorker' in navigator) {
                                        const regs = await navigator.serviceWorker.getRegistrations();
                                        await Promise.all(regs.map((reg) => reg.unregister()));
                                }
                        } catch (error) {
                                console.error('Service worker unregister failed', error);
                        }
                })();

                // Don't let a hung cache API block the reload the driver just asked for.
                await Promise.race([
                        cleanup,
                        new Promise((resolve) => setTimeout(resolve, 1500)),
                ]);

                const url = new URL(window.location.href);
                url.searchParams.set('v', latestVersion.current || Date.now());
                window.location.replace(url.toString());
        };

        return { version, updateAvailable, reloadPage };
}

export default useVersionCheck;
